import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { FC } from ".."
import { Link, SvgIcon } from ".."
import StyledContainer from "./StyledContainer"
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"

const IntroSection: FC = () => {
  return (
    <StyledContainer maxWidth={1400}>
      <Box minHeight="100vh" display="flex" flexDirection="column" justifyContent="center">
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }}
          gap={{ xs: 6, md: 10 }}
          sx={(theme) => ({
            transition: "all 0.3s ease-in-out",
            [theme.breakpoints.down("md")]: {
              padding: theme.spacing(12, 2),
            },
          })}
        >
          <SummarySection />
          <DeadlinesSection />
          <WhatsNewSection />
        </Box>
      </Box>
    </StyledContainer>
  )
}

// A sub component for the summary section
const SummarySection: FC = () => {
  return (
    <Stack
      maxWidth={{ xs: "100%", md: 375 }}
      width="100%"
      gap={{ xs: 4, md: 10 }}
      pb={{ xs: 6, md: 0 }}
      sx={(theme) => ({
        [theme.breakpoints.down("md")]: {
          borderBottom: `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
        },
      })}
    >
      <Typography
        variant="heading.md"
        sx={(theme) => ({
          color: theme.palette["primary.800"].main,
          fontWeight: 700,
          fontSize: "56px",
          lineHeight: "100%",
          [theme.breakpoints.down("md")]: {
            fontSize: "40px",
          },
        })}
      >
        {filmFestivalGlobals.introSection.title}
        <span style={{ fontStyle: "italic", fontWeight: 400 }}>
          {filmFestivalGlobals.introSection.titleKeyword}
        </span>
      </Typography>
      <Stack
        gap={{ xs: 2, md: 4 }}
        sx={(theme) => ({
          "color": theme.palette["neutral.700"].main,
          "> span": {
            fontSize: "16px",
            fontWeight: "600!important",
            lineHeight: "140%",
          },
        })}
      >
        <Typography variant="body.md">{filmFestivalGlobals.introSection.firstParagraph}</Typography>
        <Typography variant="body.md">
          {filmFestivalGlobals.introSection.secondParagraph}
        </Typography>
        <Typography variant="body.md">{filmFestivalGlobals.introSection.thirdParagraph}</Typography>
        <Typography variant="body.md">
          {filmFestivalGlobals.introSection.fourthParagraph}
          <Link
            href={filmFestivalGlobals.introSection.learnMoreLink}
            target="_blank"
            sx={(theme) => ({
              color: theme.palette["neutral.700"].main,
              fontSize: "16px!important",
              paddingBottom: 0,
              marginLeft: 1,
              fontWeight: 600,
            })}
          >
            {filmFestivalGlobals.introSection.learnMoreText}
          </Link>
        </Typography>
      </Stack>
    </Stack>
  )
}

// A sub component for the deadlines section
const DeadlinesSection: FC = () => {
  return (
    <Stack
      gap={{ xs: 0, md: 8 }}
      maxWidth={{ xs: "100%", md: 400 }}
      width="100%"
      margin="0 auto"
      sx={(theme) => ({
        [theme.breakpoints.down("md")]: {
          borderBottom: `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
        },
      })}
    >
      <Typography
        sx={(theme) => ({
          color: theme.palette["neutral.700"].main,
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "120%",
          mb: 6,
        })}
      >
        {filmFestivalGlobals.introSection.deadlines.title}
      </Typography>
      {filmFestivalGlobals.introSection.deadlines.items.map((item) => (
        <Stack key={`deadline-${item.label}`} gap={2} mb={6}>
          <Typography
            sx={(theme) => ({
              color: theme.palette["primary.800"].main,
              fontWeight: 600,
              fontSize: "24px!important",
              lineHeight: "110%",
              [theme.breakpoints.down("md")]: {
                fontSize: "16px!important",
              },
            })}
          >
            {item.date}
          </Typography>
          <Typography
            sx={(theme) => ({
              color: theme.palette["neutral.700"].main,
              fontWeight: 600,
              fontSize: "24px!important",
              lineHeight: "130%",
              [theme.breakpoints.down("md")]: {
                fontSize: "16px!important",
              },
            })}
          >
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

// A sub component for the Whats new section
const WhatsNewSection: FC = () => {
  const items = filmFestivalGlobals.introSection.whatsNew.items
  return (
    <Stack gap={{ xs: 0, md: 8 }} maxWidth={{ xs: "100%", md: 400 }} width="100%" margin="0 auto">
      <Typography
        sx={(theme) => ({
          color: theme.palette["neutral.700"].main,
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "120%",
          mb: 6,
        })}
      >
        {filmFestivalGlobals.introSection.whatsNew.title}
      </Typography>
      {items.map((item, index) => (
        <Box
          key={`whats-new-${item.title}`}
          pb={6}
          sx={(theme) => ({
            display: "flex",
            alignItems: "end",
            justifyContent: "space-between",
            gap: 4,
            position: "relative",
            ...(item.link
              ? {
                  "&:hover": {
                    ".whats-new-title": {
                      textDecorationColor: theme.palette["primary.800"].main,
                    },
                    "> svg": {
                      stroke: theme.palette["primary.800"].main,
                    },
                  },
                }
              : {}),
            borderBottom:
              index !== items.length - 1
                ? `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`
                : "none",
            [theme.breakpoints.down("md")]: {
              marginBottom: 6,
            },
          })}
        >
          {item.link && <Link href={item.link} type="overlay" target="_blank" />}
          <Stack gap={2}>
            <Typography
              sx={(theme) => ({
                color: theme.palette["neutral.700"].main,
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "140%",
              })}
            >
              {item.date}
            </Typography>
            <Typography
              className="whats-new-title"
              sx={(theme) => ({
                color: theme.palette["neutral.700"].main,
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "140%",
                transition: "all 0.3s ease-in-out",
                textDecoration: "underline",
                textDecorationColor: "transparent",
                textUnderlineOffset: "2px",
              })}
            >
              {item.title}
            </Typography>
          </Stack>
          <SvgIcon
            className="whats-new-arrow"
            variant="arrowCircleUpRight"
            viewBox="0 0 20 20"
            sx={(theme) => ({
              fill: "none",
              stroke: theme.palette["neutral.700"].main,
              transition: "stroke 0.3s ease-in-out",
            })}
          />
        </Box>
      ))}
    </Stack>
  )
}

export default IntroSection
